<template>
  <div v-if="busy" class="contract-operation-blocker" aria-busy="true"></div>
  <Dialog v-if="visible" :visible="true" class="contract-preview-dialog" :showHeader="false" :closable="false" :closeOnEscape="false" :style="{ width: '90vw', height: '90vh' }" :dismissableMask="false" :modal="true" :draggable="false">
    <div class="contract-preview-layout">
      <div v-if="desktop && !manual" class="contract-preview-document">
        <div class="contract-preview-documents">
          <div v-for="document in documents" :key="document.url" class="pdf-preview-frame">
            <div class="contract-preview-document-title">{{ document.name }}</div>
            <iframe :src="document.url + '#view=FitH'" :title="'Anteprima PDF ' + document.name"></iframe>
          </div>
        </div>
      </div>
      <div class="contract-preview-actions">
        <div v-if="showGeneratedMessage" class="text-center py-2 text-500 font-medium font-italic"><h4>É stato generato il contratto #{{ matriceId }}</h4></div>
        <div class="contract-preview-main-actions">
          <Button v-if="canSign && !manual" label="Richiedi Firma OTP" icon="pi pi-pencil" class="p-button-success contract-sign-button" :loading="signing" :disabled="busy" @click="requestSignature" />
          <InlineMessage v-if="signing" severity="info">Invio della richiesta di firma in corso</InlineMessage>
          <InlineMessage v-if="manual" severity="success">Contratto firmato cartaceo. Completamento automatico tra {{ manualCountdown }} secondi.</InlineMessage>
          <Button v-if="!desktop && !manual" label="Scarica di nuovo i documenti" icon="pi pi-download" :disabled="busy" @click="$emit('download')" />
        </div>
        <div class="contract-preview-footer">
          <Button v-if="!manual" :label="'Modifica contratto #' + matriceId" icon="pi pi-pencil" class="p-button-sm p-button-warning" :disabled="busy" @click="editContract" />
          <Button :label="manual ? 'Completa caricamento contratto firmato cartaceo' : 'Salva bozza e continua dopo'" icon="pi pi-save" class="p-button-secondary contract-draft-button" :disabled="busy" @click="saveDraft" />
        </div>
      </div>
    </div>
  </Dialog>
</template>
<script>
import { ref, watch, nextTick, getCurrentInstance, onBeforeUnmount, onDeactivated } from 'vue'
import Service from '@/services/ask-pratiche/pratiche-wizard'
import { useToast } from 'primevue/usetoast'
export default {
  props: { visible: Boolean, documents: { type: Array, default: () => [] }, matriceId: [Number, String], desktop: Boolean, canSign: Boolean, manual: Boolean, showGeneratedMessage: { type: Boolean, default: false } },
  emits: ['update:visible', 'signed', 'draft', 'edit', 'download'],
  setup(props, { emit, expose }) {
    const service = new Service(getCurrentInstance().proxy.$eventBus)
    const toast = useToast()
    const busy = ref(false), signing = ref(false)
    let closingEnvelope = null
    let closingAnimations = []
    const clearClosingAnimation = () => {
      closingAnimations.forEach(animation => animation.cancel())
      closingAnimations = []
      if (closingEnvelope) closingEnvelope.remove()
      closingEnvelope = null
    }
    onDeactivated(clearClosingAnimation)
    onBeforeUnmount(clearClosingAnimation)
    const animateSignatureSent = async () => {
      if (!await service.getContractAnimationsEnabled()) return
      const dialog = document.querySelector('.contract-preview-dialog')
      if (!dialog || !dialog.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
      const bounds = dialog.getBoundingClientRect()
      const envelope = document.createElement('div')
      envelope.className = 'contract-closing-envelope'
      envelope.setAttribute('aria-hidden', 'true')
      envelope.innerHTML = '<div class="contract-envelope-paper"></div><div class="contract-envelope-front"></div><div class="contract-envelope-flap"></div><div class="contract-envelope-seal"></div>'
      envelope.style.left = `${bounds.left + bounds.width / 2 - 90}px`
      envelope.style.top = `${bounds.top + bounds.height / 2 - 60}px`
      document.body.appendChild(envelope)
      closingEnvelope = envelope
      const play = (element, frames, options) => {
        const animation = element.animate(frames, {...options, fill: 'forwards'})
        closingAnimations.push(animation)
        return animation.finished
      }
      try {
        await Promise.all([
          play(dialog, [
            {transform: 'scale(1)', opacity: 1, offset: 0},
            {transform: 'scale(0.85, 0.7)', opacity: 0.9, offset: 0.35},
            {transform: `scale(${150 / bounds.width}, ${95 / bounds.height})`, opacity: 0, offset: 1}
          ], {duration: 520, easing: 'cubic-bezier(0.45, 0, 0.25, 1)'}),
          play(envelope, [{opacity: 0, transform: 'scale(0.85)'}, {opacity: 1, transform: 'scale(1)'}], {duration: 520, delay: 100})
        ])
        await play(envelope.querySelector('.contract-envelope-paper'), [
          {transform: 'translateY(-42px)', opacity: 1},
          {transform: 'translateY(18px)', opacity: 0.3}
        ], {duration: 280, easing: 'ease-in-out'})
        await play(envelope.querySelector('.contract-envelope-flap'), [
          {transform: 'rotateX(-160deg)', filter: 'brightness(1.08)'},
          {transform: 'rotateX(-75deg)', filter: 'brightness(0.9)', offset: 0.5},
          {transform: 'rotateX(0deg)', filter: 'brightness(1)'}
        ], {duration: 380, easing: 'cubic-bezier(0.4, 0, 0.2, 1)'})
        await Promise.all([
          play(envelope.querySelector('.contract-envelope-seal'), [{opacity: 0, transform: 'scale(1.5)'}, {opacity: 1, transform: 'scale(1)'}], {duration: 180}),
          play(envelope, [{transform: 'scale(1)'}, {transform: 'scale(0.96)', offset: 0.5}, {transform: 'scale(1)'}], {duration: 180})
        ])
        const dx = window.innerWidth + 180 - (bounds.left + bounds.width / 2)
        const dy = -80
        const flight = Array.from({length: 41}, (_, index) => {
          const t = index / 40
          const x = dx * t
          const y = dy * t - 100 * 4 * t * (1 - t)
          const scale = 1 - 0.25 * t
          const rotation = -12 * Math.sin(Math.PI * t)
          return {transform: `translate(${x}px, ${y}px) scale(${scale}) rotate(${rotation}deg)`, opacity: 1, offset: t}
        })
        const mask = dialog.closest('.p-dialog-mask')
        if (mask) {
          play(mask, [{backgroundColor: getComputedStyle(mask).backgroundColor}, {backgroundColor: 'rgba(0, 0, 0, 0)'}], {duration: 850}).catch(() => {})
        }
        await play(envelope, flight, {duration: 850, easing: 'cubic-bezier(0.35, 0, 0.25, 1)'})
      } catch (error) {
        // Cancelling on navigation also rejects Animation.finished.
        if (error.name !== 'AbortError') console.warn('Animazione del contratto non disponibile', error)
      }
    }
    const animateDraftToHistory = async () => {
      if (!await service.getContractAnimationsEnabled()) return
      const dialog = document.querySelector('.contract-preview-dialog')
      if (!dialog || !dialog.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
      const bounds = dialog.getBoundingClientRect()
      const historyLink = Array.from(document.querySelectorAll('.layout-menu-container a[href]')).find(link => {
        const rect = link.getBoundingClientRect()
        return new URL(link.href, window.location.href).pathname === '/caricamenti-multisito'
          && rect.width > 0 && rect.height > 0 && rect.left >= 0 && rect.right <= window.innerWidth
          && rect.top >= 0 && rect.bottom <= window.innerHeight
      })
      const destination = historyLink?.getBoundingClientRect()
      const card = document.createElement('div')
      card.className = 'contract-saved-draft'
      card.setAttribute('aria-hidden', 'true')
      card.innerHTML = '<div class="contract-draft-paper"></div><div class="contract-draft-cover"><i class="pi pi-folder"></i><span>Bozza salvata</span><i class="pi pi-check-circle"></i></div>'
      if (props.manual) card.querySelector('span').textContent = 'Contratto firmato salvato'
      card.style.left = `${bounds.left + bounds.width / 2 - 110}px`
      card.style.top = `${bounds.top + bounds.height / 2 - 60}px`
      document.body.appendChild(card)
      closingEnvelope = card
      const play = (element, frames, options) => {
        const animation = element.animate(frames, {...options, fill: 'forwards'})
        closingAnimations.push(animation)
        return animation.finished
      }
      try {
        await Promise.all([
          play(dialog, [{opacity: 1, transform: 'scale(1)'}, {opacity: 0, transform: 'scale(0.9)'}], {duration: 350, easing: 'ease-in'}),
          play(card, [{opacity: 0, transform: 'scale(0.75)'}, {opacity: 1, transform: 'scale(1)'}], {duration: 400, easing: 'ease-out'})
        ])
        await play(card.querySelector('.contract-draft-cover'), [
          {transform: 'rotateY(-140deg)'},
          {transform: 'rotateY(-65deg)', offset: 0.5},
          {transform: 'rotateY(0deg)'}
        ], {duration: 420, easing: 'cubic-bezier(0.4, 0, 0.2, 1)'})
        await play(card.querySelector('.pi-check-circle'), [{transform: 'scale(0)'}, {transform: 'scale(1.15)', offset: 0.7}, {transform: 'scale(1)'}], {duration: 250})
        const dx = (destination ? destination.left + destination.width / 2 : 28) - (bounds.left + bounds.width / 2)
        const dy = (destination ? destination.top + destination.height / 2 : 80) - (bounds.top + bounds.height / 2)
        const flight = Array.from({length: 41}, (_, index) => {
          const t = index / 40
          return {transform: `translate(${dx * t}px, ${dy * t - 70 * 4 * t * (1 - t)}px) scale(${1 - 0.78 * t}) rotate(${-8 * Math.sin(Math.PI * t)}deg)`, opacity: 1, offset: t}
        })
        const mask = dialog.closest('.p-dialog-mask')
        if (mask) play(mask, [{backgroundColor: getComputedStyle(mask).backgroundColor}, {backgroundColor: 'rgba(0, 0, 0, 0)'}], {duration: 850}).catch(() => {})
        await play(card, flight, {duration: 850, easing: 'cubic-bezier(0.35, 0, 0.25, 1)'})
        await Promise.all([
          play(card, [{transform: `translate(${dx}px, ${dy}px) scale(0.22)`, opacity: 1}, {transform: `translate(${dx}px, ${dy}px) scale(0.05)`, opacity: 0}], {duration: 220, easing: 'ease-in'}),
          ...(historyLink ? [play(historyLink, [
            {boxShadow: '0 0 0 0 rgba(142, 187, 176, 0)', backgroundColor: getComputedStyle(historyLink).backgroundColor},
            {boxShadow: '0 0 0 6px rgba(142, 187, 176, 0.35)', backgroundColor: 'rgba(142, 187, 176, 0.3)', offset: 0.4},
            {boxShadow: '0 0 0 12px rgba(142, 187, 176, 0)', backgroundColor: getComputedStyle(historyLink).backgroundColor}
          ], {duration: 450, easing: 'ease-out'})] : [])
        ])
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('Animazione del contratto non disponibile', error)
      }
    }

    const animateInlineSignatureSent = async (source) => {
      if (!await service.getContractAnimationsEnabled()) return
      if (!document.body.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
      const bounds = source ?? { left: window.innerWidth / 2, top: window.innerHeight / 2, width: 0, height: 0 }
      const envelope = document.createElement('div')
      envelope.className = 'contract-closing-envelope'
      envelope.setAttribute('aria-hidden', 'true')
      envelope.innerHTML = '<div class="contract-envelope-paper"></div><div class="contract-envelope-front"></div><div class="contract-envelope-flap"></div><div class="contract-envelope-seal"></div>'
      envelope.style.left = `${bounds.left + bounds.width / 2 - 90}px`
      envelope.style.top = `${bounds.top + bounds.height / 2 - 60}px`
      document.body.appendChild(envelope)
      closingEnvelope = envelope
      const play = (element, frames, options) => {
        const animation = element.animate(frames, {...options, fill: 'forwards'})
        closingAnimations.push(animation)
        return animation.finished
      }
      try {
        await play(envelope, [{opacity: 0, transform: 'scale(0.85)'}, {opacity: 1, transform: 'scale(1)'}], {duration: 350})
        await play(envelope.querySelector('.contract-envelope-paper'), [
          {transform: 'translateY(-42px)', opacity: 1},
          {transform: 'translateY(18px)', opacity: 0.3}
        ], {duration: 280, easing: 'ease-in-out'})
        await play(envelope.querySelector('.contract-envelope-flap'), [
          {transform: 'rotateX(-160deg)', filter: 'brightness(1.08)'},
          {transform: 'rotateX(-75deg)', filter: 'brightness(0.9)', offset: 0.5},
          {transform: 'rotateX(0deg)', filter: 'brightness(1)'}
        ], {duration: 380, easing: 'cubic-bezier(0.4, 0, 0.2, 1)'})
        await Promise.all([
          play(envelope.querySelector('.contract-envelope-seal'), [{opacity: 0, transform: 'scale(1.5)'}, {opacity: 1, transform: 'scale(1)'}], {duration: 180}),
          play(envelope, [{transform: 'scale(1)'}, {transform: 'scale(0.96)', offset: 0.5}, {transform: 'scale(1)'}], {duration: 180})
        ])
        const dx = window.innerWidth + 180 - (bounds.left + bounds.width / 2)
        const dy = -80
        const flight = Array.from({length: 41}, (_, index) => {
          const t = index / 40
          const x = dx * t
          const y = dy * t - 100 * 4 * t * (1 - t)
          const scale = 1 - 0.25 * t
          const rotation = -12 * Math.sin(Math.PI * t)
          return {transform: `translate(${x}px, ${y}px) scale(${scale}) rotate(${rotation}deg)`, opacity: 1, offset: t}
        })
        await play(envelope, flight, {duration: 850, easing: 'cubic-bezier(0.35, 0, 0.25, 1)'})
      } catch (error) {
        // Cancelling on navigation also rejects Animation.finished.
        if (error.name !== 'AbortError') console.warn('Animazione invio firma non disponibile', error)
      } finally {
        clearClosingAnimation()
      }
    }

    const close = async () => {
      emit('update:visible', false)
      await nextTick()
      clearClosingAnimation()
    }
    const finish = async (kind, source) => {
      if (busy.value) return
      clearManualTimer()
      busy.value = true
      const wasVisible = props.visible
      try {
        if (kind === 'draft') await animateDraftToHistory()
        else if (props.visible) await animateSignatureSent()
        else await animateInlineSignatureSent(source)
        await close()
        if (wasVisible) emit(kind)
      } finally { busy.value = false }
    }
    const requestSignature = async () => {
      if (busy.value) return
      busy.value = true
      signing.value = true
      try {
        const response = await service.signPdfMatrice(props.matriceId)
        if (!response.items?.success) {
          toast.add({ severity: 'error', detail: response.items?.errore || 'Richiesta di firma non riuscita' })
          return
        }
        toast.add({ severity: 'success', detail: response.items.errore })
        signing.value = false
        await animateSignatureSent()
        await close()
        emit('signed')
      } finally { signing.value = false; busy.value = false }
    }
    const saveDraft = () => finish('draft')
    const manualCountdown = ref(5)
    let manualTimer = null
    const clearManualTimer = () => {
      if (manualTimer !== null) clearInterval(manualTimer)
      manualTimer = null
    }
    watch(() => [props.visible, props.manual, props.showGeneratedMessage, props.matriceId], () => {
      clearManualTimer()
      manualCountdown.value = 5
      if (!props.visible || !props.manual || !props.showGeneratedMessage) return
      manualTimer = setInterval(() => {
        manualCountdown.value -= 1
        if (manualCountdown.value === 0) {
          clearManualTimer()
          saveDraft().catch(error => {
            toast.add({ severity: 'error', detail: error.message || 'Impossibile completare il caricamento', life: 7000 })
          })
        }
      }, 1000)
    }, { immediate: true })
    onDeactivated(clearManualTimer)
    onBeforeUnmount(clearManualTimer)
    const editContract = async () => {
      if (busy.value) return
      busy.value = true
      try {
        await service.detachContractSignature(props.matriceId)
        await close()
        emit('edit', props.matriceId)
      } catch (error) {
        toast.add({ severity: 'error', detail: error.response?.data?.message || 'Impossibile sganciare la richiesta di firma', life: 7000 })
      } finally { busy.value = false }
    }
    expose({ finish })
    return { busy, signing, manualCountdown, requestSignature, saveDraft, editContract }
  }
}
</script>
<style>
.contract-preview-dialog .p-dialog-content { flex: 1; min-height: 0; overflow: auto; padding: 0; }

.contract-preview-layout { display: flex; gap: 0; height: 100%; min-height: 0; }
.contract-preview-document { flex: 2 1 0; min-width: 0; overflow-y: auto; padding: 0.6rem; box-sizing: border-box; }
.contract-preview-documents { height: 100%; }
.contract-preview-document-title { padding: 0.5rem; font-weight: bold; }
.contract-preview-document .pdf-preview-frame,
.contract-preview-document iframe { width: 100%; height: 100%; }
.contract-preview-document .pdf-preview-frame { display: flex; flex-direction: column; margin-bottom: 1rem; }
.contract-preview-document iframe { border: 0; min-height: 350px; flex: 1; }
.contract-preview-actions { flex: 1 1 0; box-sizing: border-box; display: flex; flex-direction: column; gap: 2rem; min-width: 0; padding: 1.5rem; }
.contract-preview-main-actions { flex: 1; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 1.5rem; }
.contract-sign-button { width: 100%; max-width: 32rem; min-height: 100px; font-size: 1.4rem; }
.contract-preview-secondary-actions { display: flex; flex-direction: column; align-self: stretch; align-items: flex-start; gap: 0.75rem; margin-top: 1.5rem; }
.contract-preview-secondary-actions .p-button { width: 100%; max-width: 22rem; }
.contract-preview-secondary-actions .p-button-label { white-space: normal; overflow-wrap: anywhere; }
.contract-preview-footer { padding-top: 2rem; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem; }
.contract-draft-button { margin-left: auto; }
.contract-preview-placeholder { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1rem; }
@media (max-width: 1199px) {
  .contract-preview-layout { height: auto; min-height: 100%; }
  .contract-preview-actions { flex-basis: 100%; padding: 1.5rem; }
  .contract-sign-button { min-height: 80px; font-size: 1.2rem; }
}
</style>

<style>
.contract-closing-envelope { position: fixed; width: 180px; height: 120px; z-index: 2147483647; pointer-events: none; perspective: 600px; opacity: 0; border-radius: 8px; background: #d8e9e5; box-shadow: 0 12px 35px #0003; }
.contract-envelope-paper { position: absolute; inset: 10px 20px; background: repeating-linear-gradient(white 0 12px, #dce7e4 12px 14px, white 14px 24px); border: 8px solid white; border-radius: 4px; transform: translateY(-42px); }
.contract-envelope-front { position: absolute; inset: 0; background: #8ebbb0; clip-path: polygon(0 0, 50% 55%, 100% 0, 100% 100%, 0 100%); border-radius: 8px; }
.contract-envelope-flap { position: absolute; inset: 0; background: #b6d5cb; clip-path: polygon(0 0, 100% 0, 50% 58%); transform-origin: top; transform: rotateX(-160deg); backface-visibility: visible; }
</style>

<style>
.contract-envelope-seal { position: absolute; left: 78px; top: 54px; width: 24px; height: 24px; border-radius: 50%; background: #215159; box-shadow: inset 0 0 0 3px #ffffff40; opacity: 0; }
</style>

<style>
.contract-saved-draft { position: fixed; z-index: 2147483647; width: 220px; height: 120px; display: flex; align-items: center; justify-content: center; gap: 0.75rem; background: #f3f4f6; color: #374151; border: 1px solid #d1d5db; border-radius: 12px; box-shadow: 0 12px 35px #0003; pointer-events: none; opacity: 0; }
.contract-saved-draft .pi-folder { font-size: 1.5rem; }
.contract-saved-draft .pi-check-circle { color: #22a06b; font-size: 1.5rem; transform: scale(0); }
</style>

<style>
.contract-saved-draft { perspective: 700px; background: #d1d5db; }
.contract-draft-paper { position: absolute; inset: 8px 8px 8px 18px; background: repeating-linear-gradient(white 0 14px, #e5e7eb 14px 16px, white 16px 26px); border: 6px solid white; border-radius: 4px; }
.contract-draft-cover { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: 0.75rem; background: #e5e7eb; border: 1px solid #b8bec7; border-radius: 8px; border-left: 9px solid #9ca3af; transform-origin: left center; transform: rotateY(-140deg); backface-visibility: visible; }
</style>

<style>
.contract-preview-dialog { background: white; }
.contract-operation-blocker { position: fixed; inset: 0; z-index: 2147483646; cursor: wait; }
</style>
